import type { LibraryStore } from '@orbit-mind/engine-react';
import { downloadMapBackup } from '../engine/backup';
import { duplicateLibraryMap, readLibraryDocument, renameLibraryMap } from '@orbit-mind/map-engine';
import {
  ok,
  unwrap,
  type AppError,
  type MapId,
  type Result,
  type Timestamp,
} from '@orbit-mind/shared-types';
import {
  createHttpOrganizationTransport,
  syncOrganization,
  type OrganizationSyncOutcome,
} from '@orbit-mind/sync-engine';
import { localStorageLayer, storageAccount, type LocalStorageLayer } from '../engine/storage';
import { LOCAL_AUTHOR } from '../engine/useOpenMap';
import type { MapDocument } from '@orbit-mind/domain';

function setting(layer: LocalStorageLayer, key: string): LibraryStore {
  return {
    read: async () => (await layer.db.settings.get(key))?.value,
    write: async (value) => {
      await layer.db.settings.put({ key, value });
    },
  };
}

export function libraryStore(layer: LocalStorageLayer): LibraryStore {
  return setting(layer, 'library-organization');
}

/** What this browser and the server last agreed on; the base of the next merge. */
export function syncedLibraryStore(layer: LocalStorageLayer): LibraryStore {
  return setting(layer, 'library-organization-synced');
}

/**
 * Folders, tags and favourites, merged with the account's other devices.
 *
 * Signed out there is nothing to do and nothing failed. Stops early if the account changes while
 * it runs: another account's organization is never merged into this one.
 */
export async function syncOrganizationOf(
  layer: LocalStorageLayer,
  isCancelled: () => boolean,
): Promise<Result<OrganizationSyncOutcome, AppError>> {
  const account = storageAccount();
  if (account === null) return ok({ sent: false, received: false, revision: 0 });
  return syncOrganization({
    transport: createHttpOrganizationTransport({
      fetch: globalThis.fetch.bind(globalThis),
      accountId: account,
    }),
    local: libraryStore(layer),
    synced: syncedLibraryStore(layer),
    accountId: account,
    isCancelled: () => isCancelled() || storageAccount() !== account,
  });
}
export interface LibraryActions {
  backup: (id: MapId) => Promise<void>;
  pending: (id: string) => Promise<boolean>;
  read: (id: string) => Promise<MapDocument>;
  rename: (id: MapId, title: string) => Promise<void>;
  duplicate: (id: MapId, recovered?: MapDocument) => Promise<MapId>;
}
export function libraryActions(layer = localStorageLayer()): LibraryActions {
  return {
    backup: (id) => downloadMapBackup(layer, id),
    pending: async (id: string) =>
      (await layer.journal.readPending(id as MapId)).records.length > 0,
    read: (id: string) => readLibraryDocument(layer.journal, id as MapId),
    rename: (id: MapId, title: string) =>
      renameLibraryMap(layer.journal, layer.catalog, id, LOCAL_AUTHOR, title),
    duplicate: (id: MapId, recovered?: MapDocument) =>
      duplicateLibraryMap(
        layer.journal,
        layer.catalog,
        id,
        async (copy, source) => {
          for (const [oldId, newId] of copy.attachmentIds) {
            const attachment = unwrap(await layer.attachments.get(oldId));
            if (attachment === undefined)
              throw new Error('Sincronize os anexos antes de duplicar.');
            const bytes = unwrap(await layer.attachments.bytesOf(attachment.storageKey));
            if (bytes === undefined) throw new Error('Anexo indisponível neste dispositivo.');
            unwrap(
              await layer.attachments.add({
                mapId: copy.document.map.id,
                nodeId: copy.nodeIds.get(attachment.nodeId) ?? attachment.nodeId,
                workspaceId: source.map.workspaceId,
                attachmentId: newId,
                file: bytes,
                filename: attachment.filename,
                now: Date.now() as Timestamp,
                by: LOCAL_AUTHOR,
              }),
            );
          }
        },
        recovered,
      ),
  };
}
